import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import { formatDateOnly, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { LeaveCategoryBreakdownTable, useGetLeaveOverview } from '@/modules/leave/hr-leave-manager';

const StatCard = ({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: number;
  valueClassName?: string;
}) => (
  <Card>
    {/* On a phone the value sits beside its title, on one line. */}
    <CardContent className="flex flex-col gap-1 max-md:flex-row max-md:items-center max-md:justify-between max-md:gap-3 md:p-5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={cn('text-2xl font-semibold', valueClassName)}>{value}</span>
    </CardContent>
  </Card>
);

/** The signed-in user's own Leave Overview (current HR term): totals of the limited categories, then
 * the per-category breakdown. */
export const MyLeaveOverview = ({ userId }: { userId: string }) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetLeaveOverview({
    pageNo: 1,
    pageSize: 1,
    userIds: [userId],
  });
  const overview = data?.items[0];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }
  if (error)
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error ? error.message : String(error)}
      </p>
    );
  if (!overview) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {t('NO_CURRENT_HR_TERM')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* All four always show; on a phone each is one line. */}
      <div className="grid grid-cols-1 gap-4 max-md:gap-2 md:grid-cols-4">
        <StatCard label={t('ALLOCATED_LEAVE')} value={overview.TotalAllocated} />
        <StatCard label={t('TAKEN_LEAVES')} value={overview.TotalTaken} />
        <StatCard label={t('REQUESTED_LEAVES')} value={overview.TotalRequested} />
        <StatCard
          label={t('REMAINING_LEAVE')}
          value={overview.TotalRemaining}
          valueClassName="text-primary-600"
        />
      </div>

      <Card>
        {/* The HR period on the right of the title; on a narrow screen it wraps under it. */}
        <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 pb-2 md:pb-2">
          <CardTitle className="text-base">{t('LEAVE_BY_CATEGORY')}</CardTitle>
          <CardDescription>
            {t('HR_PERIOD')}: {formatDateOnly(toDateOnlyValue(overview.PeriodStartDate))} -{' '}
            {formatDateOnly(toDateOnlyValue(overview.PeriodEndDate))}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 md:pt-0">
          <LeaveCategoryBreakdownTable categories={overview.Categories} />
        </CardContent>
      </Card>
    </div>
  );
};
